import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const { t } = useTranslation();
  const [contactInfo, setContactInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ loading: false, success: false, error: '' });
  const [visibleSections, setVisibleSections] = useState({});

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.15, rootMargin: '-80px' }
    );

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get('/api/contact');
        setContactInfo(response.data.data);
      } catch (error) {
        setContactInfo(staticContactInfo);
      }
    };

    fetchContactInfo();
  }, []);

  const staticContactInfo = {
    email: { sales: 'sales@almedequipment.com', marketing: 'marketing@almedequipment.com' },
    phone: { primary: '+91 93225 10636', secondary: '+91 93225 10635' },
    address: {
      full: 'Gala No. 16, Ekta Industrial Estate Khan Compound, Tondare, Taloja, Panvel, Navi Mumbai, Maharashtra 410208'
    },
    hours: { office: 'Mon - Sat: 9:00 - 18:00', site: '24 X 7', emergency: '24 X 7' },
    socialMedia: {
      facebook: 'https://facebook.com/almedequip',
      twitter: 'https://twitter.com/almedequip',
      instagram: 'https://instagram.com/almedequip',
      linkedin: 'https://linkedin.com/almedequip'
    }
  };

  const displayContact = contactInfo || staticContactInfo;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, success: false, error: '' });

    try {
      // 1. Save to Notion via backend
      await axios.post('/api/contact/submit', formData);
      
      // 2. Send email notification via EmailJS
      // EmailJS credentials - replace with your own from emailjs.com
      const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_almed';
      const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_contact';
      const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';

      if (EMAILJS_PUBLIC_KEY) {
        try {
          await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            {
              from_name: formData.name,
              from_email: formData.email,
              phone: formData.phone || 'Not provided',
              subject: formData.subject || 'Website Inquiry',
              message: formData.message,
              to_email: 'sales@almedequipment.com',
            },
            EMAILJS_PUBLIC_KEY
          );
          console.log('✅ Email sent successfully');
        } catch (emailError) {
          console.log('⚠️ Email sending skipped:', emailError.message);
        }
      }

      setSubmitStatus({ loading: false, success: true, error: '' });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({ loading: false, success: false, error: 'Failed to send message. Please try again.' });
    }
  };

  return (
    <div className="contact-page" style={{marginTop: '70px'}}>
      {/* Hero Section */}
      <section className="hero" style={{minHeight: '40vh'}}>
        <div className="hero-content">
          <p className="hero-subtitle">{t('contact.badge')}</p>
          <h1 className="hero-title">{t('contact.title')}</h1>
          <p className="hero-description">
            {t('contact.description')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-form" className="section contact-section animate-section">
        <div className="container">
          <div 
            className="contact-grid"
            style={{
              opacity: visibleSections['contact-form'] ? 1 : 0,
              transform: visibleSections['contact-form'] ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {/* Contact Information */}
            <div className="contact-info">
              <h2 style={{marginBottom: 'var(--spacing-xl)'}}>{t('contact.info.title')}</h2>
              
              <div className="contact-info-item">
                <div className="contact-info-icon">📍</div>
                <div className="contact-info-content">
                  <h4>{t('contact.info.location')}</h4>
                  <p>{displayContact.address.full}</p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">📞</div>
                <div className="contact-info-content">
                  <h4>{t('contact.info.phone')}</h4>
                  <p>
                    <a href={`tel:${displayContact.phone.primary.replace(/\s/g, '')}`}>
                      {displayContact.phone.primary}
                    </a>
                    <br />
                    <a href={`tel:${displayContact.phone.secondary.replace(/\s/g, '')}`}>
                      {displayContact.phone.secondary}
                    </a>
                  </p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">📧</div>
                <div className="contact-info-content">
                  <h4>{t('contact.info.email')}</h4>
                  <p>
                    <a href={`mailto:${displayContact.email.sales}`}>{displayContact.email.sales}</a>
                    <br />
                    <a href={`mailto:${displayContact.email.marketing}`}>{displayContact.email.marketing}</a>
                  </p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">🕐</div>
                <div className="contact-info-content">
                  <h4>{t('contact.info.workingHours')}</h4>
                  <p>
                    {t('contact.info.office')}: {displayContact.hours.office}<br />
                    {t('contact.info.siteService')}: {displayContact.hours.site}<br />
                    {t('contact.info.emergency')}: {displayContact.hours.emergency}
                  </p>
                </div>
              </div>

              {/* Social Media */}
              <div style={{marginTop: 'var(--spacing-xl)'}}>
                <h4 style={{marginBottom: 'var(--spacing-md)'}}>{t('contact.info.followUs')}</h4>
                <div style={{display: 'flex', gap: 'var(--spacing-md)'}}>
                  <a href={displayContact.socialMedia.facebook} target="_blank" rel="noopener noreferrer" 
                     style={{fontSize: '1.5rem'}}>📘</a>
                  <a href={displayContact.socialMedia.twitter} target="_blank" rel="noopener noreferrer"
                     style={{fontSize: '1.5rem'}}>🐦</a>
                  <a href={displayContact.socialMedia.instagram} target="_blank" rel="noopener noreferrer"
                     style={{fontSize: '1.5rem'}}>📷</a>
                  <a href={displayContact.socialMedia.linkedin} target="_blank" rel="noopener noreferrer"
                     style={{fontSize: '1.5rem'}}>💼</a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form">
              <h2 style={{marginBottom: 'var(--spacing-xl)'}}>{t('contact.form.title')}</h2>
              
              {submitStatus.success && (
                <div style={{
                  padding: 'var(--spacing-md)',
                  background: '#d4edda',
                  color: '#155724',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--spacing-lg)'
                }}>
                  Thank you! Your message has been sent. We'll get back to you shortly.
                </div>
              )}

              {submitStatus.error && (
                <div style={{
                  padding: 'var(--spacing-md)',
                  background: '#f8d7da',
                  color: '#721c24',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--spacing-lg)'
                }}>
                  {submitStatus.error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">{t('contact.form.name')} *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('contact.form.email')} *</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('contact.form.phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('contact.form.subject')}</label>
                  <input
                    type="text"
                    name="subject"
                    className="form-input"
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('contact.form.message')} *</label>
                  <textarea
                    name="message"
                    className="form-textarea"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  style={{width: '100%'}}
                  disabled={submitStatus.loading}
                >
                  {submitStatus.loading ? 'Sending...' : t('contact.form.send')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section id="cta" className="cta-section animate-section">
        <div 
          className="container"
          style={{
            opacity: visibleSections.cta ? 1 : 0,
            transform: visibleSections.cta ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <h2 className="cta-title">{t('contact.emergency.title')}</h2>
          <p className="cta-description">
            {t('contact.emergency.description')}
          </p>
          <a href="tel:+919322510636" className="btn btn-white">
            📞 {t('contact.emergency.callNow')}: +91 93225 10636
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
